// The garage: the owner's cars, each at a glance; adding one; and any TUNE MFD in no car, to put in one.
import { Page } from '../../shell/Page';
import { isMfd } from '../../lib/words';
import { useDevices, useVehicles } from '../../queries';
import { Button, ButtonLink, EmptyState, ErrorBanner, Skeleton, useNow, useRouteSheet } from '../../ui';
import { CarIcon, PlusIcon, QrCodeIcon } from '../../ui/icons';
import { CarCards } from './CarCard';
import { AddCarSheet } from './CarSheets';
import { MfdCard } from './Mfd';
import s from './Garage.module.css';

export function Garage() {
  const now = useNow(60_000);
  const vehicles = useVehicles();
  const devices = useDevices();
  const [adding, setAdding] = useRouteSheet('add-car');
  const cars = vehicles.data?.vehicles ?? [];
  const loose = (devices.data?.devices ?? []).filter((d) => isMfd(d) && d.vehicleId === null);
  const add = (
    <Button size="small" icon={<PlusIcon size={16} weight="bold" />} onClick={() => setAdding(true)}>
      Add a car
    </Button>
  );

  return (
    <Page
      title="Garage"
      root
      subtitle="Your cars, and the TUNE MFD in each."
      headActions={cars.length > 0 ? add : undefined}
    >
      {vehicles.isError && <ErrorBanner retry={() => void vehicles.refetch()}>Your cars couldn't load.</ErrorBanner>}
      {vehicles.isPending && <Skeleton blocks={3} lines={0} label="Loading your cars" />}
      {vehicles.isSuccess && cars.length === 0 && (
        <EmptyState
          icon={<CarIcon size={56} />}
          title="No cars yet"
          action={
            <Button icon={<PlusIcon size={18} weight="bold" />} onClick={() => setAdding(true)}>
              Add a car
            </Button>
          }
        >
          Add your car, then sign in its TUNE MFD. Its settings, trips and datalogs then come here by themselves.
        </EmptyState>
      )}
      {cars.length > 0 && <CarCards cars={cars} now={now} />}

      {loose.length > 0 && (
        <section className={s.section} aria-labelledby="loose">
          <div className={s.sectionHead}>
            <h2 id="loose" className={s.sectionTitle}>
              TUNE MFDs in no car
            </h2>
          </div>
          <p className={s.sectionText}>
            Each asks which car it's in, on its own screen. Put it in one here, or sign it out.
          </p>
          <div className={s.mfds}>
            {loose.map((d) => (
              <MfdCard key={d.id} device={d} car={null} sheet={`place-${d.id}`} />
            ))}
          </div>
        </section>
      )}

      {vehicles.isSuccess && (
        <div className={s.pairing}>
          <QrCodeIcon size={22} weight="bold" aria-hidden="true" />
          <p>To sign in an MFD, open Settings on it, then TUNE account, and scan its code with your phone's camera.</p>
          <ButtonLink to="/pair" variant="gray" size="small">
            Type its code
          </ButtonLink>
        </div>
      )}

      <AddCarSheet open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}
